// ── Shared types and constants for the Create Booking wizard ──────────────────

import { TIME_SLOTS } from "../Pricing/constants";

export const EVENT_TYPES = [
  "Wedding",
  "Wedding Reception",
  "Wedding Ceremony",
  "Anniversary",
  "Engagement Party",
  "Baby Shower",
  "Bridal Shower",
  "Birthday",
  "Sweet 16",
  "Quinceañera",
  "Graduation",
  "Retirement",
  "Corporate Event",
  "Conference",
  "Seminar",
  "Workshop",
  "Fundraiser",
  "Gala",
  "Award Ceremony",
  "Holiday Party",
  "New Year's Party",
  "Cultural Event",
  "Religious Ceremony",
  "Funeral Reception",
  "Memorial",
  "Reunion",
  "Prom",
  "Other",
] as const;

export const PAYMENT_METHODS = [
  "Cash",
  "Cheque",
  "E-Transfer",
  "Credit Card",
  "Debit",
  "Other",
] as const;

export const SECURITY_METHODS = ["Cash", "Cheque", "Credit Card", "Debit"] as const;

// ── Time slot helpers ──────────────────────────────────────────────────────────

/** Get the readable time range for a slot code (e.g., "AM" → "8:00 AM – 3:00 PM") */
export const getTimeSlotLabel = (slot: string): string => {
  const found = TIME_SLOTS.find(s => s.value === slot);
  return found?.timeRange ?? slot;
};

export const getTimeSlotDisplay = (slot: string): string => {
  const found = TIME_SLOTS.find(s => s.value === slot);
  return found?.label ?? slot;
};

export const PROVINCES = [
  "AB","BC","MB","NB","NL","NS","NT","NU","ON","PE","QC","SK","YT",
] as const;

export interface CustomerForm {
  firstName: string;
  lastName: string;
  organization: string;
  email1: string;
  email2: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  phone1: string;
  phone1Who: string;
  phone2: string;
  phone2Who: string;
  phone3: string;
  phone3Who: string;
}

export interface SelectedHall {
  hallKey: string;
  hallLabel: string;
  hallCapacity: number;
}

export interface PaymentEntry {
  amount: string;
  method: string;
  paymentDate: string;
}

export interface AddonItem {
  service_id: number | null;
  itemName: string;
  category: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

export type PricingType = "per_event" | "per_person" | "per_hour" | "per_plate" | "per_unit";

export interface VendorServiceItem {
  // Item identity
  itemId: number;
  itemName: string;
  serviceId: number;
  serviceName: string;
  serviceCategory: string;
  categoryId: number | null;
  categoryName: string;
  subcategoryId: number | null;
  subcategoryName: string;

  // Vendor
  vendorId: number;
  vendorName: string;
  isDefaultVendor: boolean;

  // Pricing
  pricingType: PricingType;
  rate: number;
  quantity: number;
  hours: number;
  basePrice: number;
  finalPrice: number;
  isAdjustable: boolean;
  minPriceLimit: number | null;
}

export interface BookingFormState {
  // ── Step 1 ─────────────────────────────────────────────────────────────────
  banquetName: string;
  agentName: string;
  eventType: string;
  eventDate: string;
  timeSlot: string;
  expectedGuests: string;
  halls: SelectedHall[];
  primaryCustomer: CustomerForm;
  hasSecondaryCustomer: boolean;
  secondaryCustomer: CustomerForm;
  publicNotes: string;
  privateNotes: string;

  // ── Step 2 ─────────────────────────────────────────────────────────────────
  guestCount: string;
  childrenCount: string;
  guestMinimum: string;
  hallFlatFee: string;
  hallPricePerPerson: string;
  foodServiceOption: string;
  foodServicePricePerPerson: string;
  barServiceOption: string;
  barServiceAmount: string;
  barServiceType: string;
  bartenderCount: string;
  bartenderAmount: string;
  barPatrons: string;
  barPackageOption: string;
  barPackagePricePerPerson: string;
  lcboLicence: string;
  barHours: string;
  menuOption: string;
  menuPricePerPerson: string;
  adjustment: string;
  adjustmentReason: string;
  serviceChargePercent: string;
  gratuityPercent: string;
  securityDepositAmount: string;
  securityDepositMethod: string;
  securityDepositDate: string;
  payments: PaymentEntry[];

  // ── Step 3 ─────────────────────────────────────────────────────────────────
  addons: AddonItem[];
  vendorServices: VendorServiceItem[];

  // ── LED Screens ────────────────────────────────────────────────────────────
  selectedLEDs: string[];
}

export const EMPTY_CUSTOMER: CustomerForm = {
  firstName: "", lastName: "", organization: "",
  email1: "", email2: "",
  address: "", city: "", province: "ON", postalCode: "",
  phone1: "", phone1Who: "Cell",
  phone2: "", phone2Who: "Home",
  phone3: "", phone3Who: "Work",
};

export const INITIAL_FORM: BookingFormState = {
  banquetName: "", agentName: "", eventType: "",
  eventDate: "", timeSlot: "", expectedGuests: "", halls: [],
  primaryCustomer: { ...EMPTY_CUSTOMER },
  hasSecondaryCustomer: false,
  secondaryCustomer: { ...EMPTY_CUSTOMER },
  publicNotes: "", privateNotes: "",
  guestCount: "100", childrenCount: "0", guestMinimum: "0",
  hallFlatFee: "0", hallPricePerPerson: "0",
  foodServiceOption: "", foodServicePricePerPerson: "0",
  barServiceOption: "", barServiceAmount: "0", barServiceType: "",
  bartenderCount: "0", bartenderAmount: "0", barPatrons: "0",
  barPackageOption: "", barPackagePricePerPerson: "0",
  lcboLicence: "", barHours: "0",
  menuOption: "", menuPricePerPerson: "0",
  adjustment: "0", adjustmentReason: "",
  serviceChargePercent: "0", gratuityPercent: "0",
  securityDepositAmount: "0", securityDepositMethod: "Cash", securityDepositDate: "",
  payments: [],
  addons: [],
  vendorServices: [],
  selectedLEDs: [],
};

// ── LED pricing ──────────────────────────────────────────────────────────────
export const LED_PRICING: Record<string, number> = {
  "LED 1": 3000,
  "LED 2": 3000,
  "LED 3": 1800,
  "LED 4": 1800,
};

export function computeLEDTotal(selectedLEDs: string[], _halls: SelectedHall[]): number {
  return selectedLEDs.reduce((sum, led) => sum + (LED_PRICING[led] ?? 0), 0);
}

// ── Financial helpers ──────────────────────────────────────────────────────────
export const n = (v: string | number) => Number(v) || 0;

export const computeFinancials = (form: BookingFormState) => {
  const guests      = n(form.expectedGuests) || n(form.guestCount);
  const hallCapacity = form.halls[0]?.hallCapacity || 0;
  // Flat fee only applies when guests are below hall capacity
  const hallFlatFee = guests >= hallCapacity && hallCapacity > 0 ? 0 : n(form.hallFlatFee);
  const hallSubtotal  = hallFlatFee + n(form.hallPricePerPerson) * guests;
  const foodSubtotal  = (n(form.foodServicePricePerPerson) + n(form.menuPricePerPerson)) * guests;
  const barSubtotal   = n(form.barServiceAmount)
    + (n(form.bartenderCount) * n(form.bartenderAmount))
    + n(form.barPackagePricePerPerson) * guests;
  const addonsSubtotal = 0; // managed by Step 3
  const baseSubtotal  = hallSubtotal + foodSubtotal + barSubtotal + addonsSubtotal;
  const adjustment    = n(form.adjustment);
  const serviceChargeAmount = baseSubtotal * (n(form.serviceChargePercent) / 100);
  const gratuityAmount      = baseSubtotal * (n(form.gratuityPercent) / 100);
  const subtotal      = baseSubtotal + adjustment + serviceChargeAmount + gratuityAmount;
  const hstAmount     = subtotal * 0.13;
  const grandTotal    = subtotal + hstAmount;
  return {
    hallSubtotal, foodSubtotal, barSubtotal, addonsSubtotal,
    baseSubtotal, subtotal, serviceChargeAmount, gratuityAmount,
    hstAmount, grandTotal,
  };
};

export const fmtCAD = (n: number) =>
  n.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
